import { type Coaching, rand, fixed, percent } from '../../utils/competition'

/**
 * One driver's coaching, as driver_coaching_v1 returns it.
 *
 * The same card is on the cockpit and on the driver's own board, so it is
 * written to him in the second person either way. What is not on him is kept in
 * its own box and never mixed with the levers: a driver is never coached, or
 * put on a performance track, for something the company owns.
 */
export function CoachingCard({ coaching, audience }: { coaching: Coaching | null | undefined; audience: 'driver' | 'staff' }) {
  if (!coaching) return null
  if (coaching.state === 'no_shifts') {
    return (
      <section className="rounded-xl border border-border bg-card p-4">
        <h2 className="text-sm font-semibold">What would lift {audience === 'driver' ? 'your' : 'his'} earnings</h2>
        <p className="mt-1 text-sm text-muted-foreground">No shifts in the last four weeks, so there is nothing to coach on yet.</p>
      </section>
    )
  }
  const m = coaching.measures
  return (
    <section className="rounded-xl border border-border bg-card p-4">
      <h2 className="text-sm font-semibold">What would lift {audience === 'driver' ? 'your' : 'his'} earnings</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        From {coaching.shifts} shift{coaching.shifts === 1 ? '' : 's'} in the last four weeks, against the whole fleet on the same dates.
        {coaching.confidence === 'low' ? ' Only a few shifts have Uber hours, so treat this as a first look.' : ''}
      </p>

      {coaching.levers.length > 0 ? (
        <ol className="mt-3 space-y-3">
          {coaching.levers.map((l, i) => (
            <li key={l.key} className="rounded-lg border border-[hsl(var(--brand))]/30 bg-[hsl(var(--brand-soft))]/40 p-3">
              <p className="text-sm font-medium">
                {i + 1}. {l.title}
                {l.gain_per_shift_zar != null && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">about {rand(l.gain_per_shift_zar)} more a shift</span>
                )}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">{l.detail}</p>
            </li>
          ))}
        </ol>
      ) : coaching.keep_going ? (
        <p className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
          {audience === 'driver' ? 'You are' : 'He is'} at or above the fleet on the things a driver controls. Keep the hours and acceptance where they are.
        </p>
      ) : null}

      {coaching.not_on_you.length > 0 && (
        <div className="mt-3 rounded-lg border border-border bg-muted/50 p-3">
          <p className="text-sm font-medium">{audience === 'driver' ? 'Not on you' : 'Not on him'}</p>
          <ul className="mt-1 space-y-1.5">
            {coaching.not_on_you.map((f) => (
              <li key={f.key} className="text-sm text-muted-foreground">
                {f.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      {audience === 'staff' && coaching.staff_notes.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
          <p className="text-sm font-medium">For staff only</p>
          <ul className="mt-1 space-y-1.5">
            {coaching.staff_notes.map((f) => (
              <li key={f.key} className="text-sm text-muted-foreground">
                {f.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <Measure label="Per shift" value={rand(m.earnings_per_shift)} sub={`fleet on the same dates ${rand(m.fleet_median_on_your_dates)}`} />
        <Measure label="Rand an online hour" value={rand(m.rand_per_online_hour)} sub={`fleet ${rand(m.fleet_rand_per_online_hour_on_your_dates)}`} />
        <Measure label="Online hours" value={fixed(m.online_hours)} sub={`acceptance ${percent(m.acceptance)}`} />
        <Measure label="Average fare" value={rand(m.average_fare)} sub={m.main_zone ? `mostly around ${m.main_zone}` : 'area not known'} />
      </div>
    </section>
  )
}

function Measure({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-md bg-muted/60 p-2">
      <p className="text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">{value}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>
    </div>
  )
}
